import {Exclude} from 'class-transformer';
import {DefaultCommunicationSrcType} from '../../users';
import {UserRoleType} from '../types/user-role.type';
import {MinimalUserModel, UserModel} from './user.entity';
import {EmailValidator, isValidPhoneNumber} from '@utils';

// Export the type so it can be used by other modules
export {DefaultCommunicationSrcType};

type ComputableUserModels = UserModel | MinimalUserModel;

export class UserComputedProperties {
	// ---- Computed properties ----
	@Exclude() isTemporary: boolean = null;
	@Exclude() displayName: string = '';
	@Exclude() initials: string = '';
	@Exclude() defaultCommunicationSrc: DefaultCommunicationSrcType = DefaultCommunicationSrcType.None;
	@Exclude() communicationSrc: Record<DefaultCommunicationSrcType, string> = null;

	public computeProperties(user: DeepPartial<ComputableUserModels>): void {
		const isUserModel = UserModel.isUserModel(user);
		// Set isTemporary
		this.isTemporary = UserComputedProperties.computeIsTemporary(user, isUserModel);
		// Set defaultCommunicationSrc & communicationSrc
		Object.assign(this, UserComputedProperties.computeCommunicationSrc(user));
		// Set displayName (using computed values)
		this.displayName = UserComputedProperties.computeDisplayName(user, this.isTemporary, this.defaultCommunicationSrc, this.communicationSrc);
		// Set initials
		this.initials = this.displayName
			?.split(' ')
			.map(word => word?.[0])
			.join('')
			.toUpperCase();
	}

	private static computeIsTemporary(user: DeepPartial<UserModel>, isUserModel: boolean): boolean {
		if (isUserModel) {
			if (!user.roles) return false;
			return user.roles.includes(UserRoleType.Signer);
		}
		// try to extract the type from username if it is starts with 'T' + <email> or <phone>
		if (user.username.startsWith('T')) {
			const type = user.username.slice(1);
			if (EmailValidator.isValidEmail(type)) return true;
			else if (isValidPhoneNumber(type)) return true;
		}
		return false;
	}

	private static computeCommunicationSrc(user: DeepPartial<ComputableUserModels>): {
		defaultCommunicationSrc: DefaultCommunicationSrcType;
		communicationSrc: Record<DefaultCommunicationSrcType, string>;
	} {
		let defaultCommunicationSrc: DefaultCommunicationSrcType = DefaultCommunicationSrcType.None;
		const preferences = user.preferences;

		// Determine defaultCommunicationSrc
		if (preferences) {
			if (preferences.notifyEmail) defaultCommunicationSrc = DefaultCommunicationSrcType.notifyEmail;
			if (preferences.notifyWhatsapp) defaultCommunicationSrc = DefaultCommunicationSrcType.notifyWhatsapp;
			if (preferences.notifySMS) defaultCommunicationSrc = DefaultCommunicationSrcType.notifySMS;
		} else if (user.phonenumber) {
			defaultCommunicationSrc = DefaultCommunicationSrcType.notifySMS;
		} else if (user.email) {
			defaultCommunicationSrc = DefaultCommunicationSrcType.notifyEmail;
		}

		// Create communicationSrc map
		const communicationSrc = {
			[DefaultCommunicationSrcType.notifyEmail]: user?.email,
			[DefaultCommunicationSrcType.notifyWhatsapp]: user?.phonenumber,
			[DefaultCommunicationSrcType.notifySMS]: user?.phonenumber,
			[DefaultCommunicationSrcType.None]: null,
		};

		return {defaultCommunicationSrc, communicationSrc};
	}

	private static computeDisplayName(
		user: DeepPartial<ComputableUserModels>,
		isTemporary: boolean,
		defaultCommunicationSrc: DefaultCommunicationSrcType,
		communicationSrc: Record<DefaultCommunicationSrcType, string>,
	): string {
		if (isTemporary) {
			return user?.fullname || communicationSrc[defaultCommunicationSrc] || user?.username || '';
		}
		return user?.fullname || user?.username || communicationSrc[defaultCommunicationSrc] || '';
	}
}
